import { useEffect, useState } from 'react';

/**
 * A logo, without the empty square around it.
 *
 * A brand keeps a logo as a big square with the drawing somewhere in the middle — on
 * nothing, or on a flat colour of its own — so set in a tile it comes out a third of the
 * size the tile has room for, and a logo on white sits in the tile as a white box. This
 * finds the box the drawing actually fills and hands back that alone, and for a logo on a
 * flat colour says what the colour is, so the tile can be the same colour and the square
 * disappears.
 *
 * Measured from a small rendition of its own (256 px, always asked for cross-origin, so
 * its cached copy always carries the header a canvas needs — see `corsReadUrl`). A
 * picture that cannot be read, or is neither on nothing nor on one colour (a photograph),
 * is left as it is: a tile showing the logo small is better than one showing nothing.
 */

const MEASURE = 'width=256,height=256,fit=scale-down,format=png';
/** Alpha below this is the soft edge of nothing. */
const SOLID = 12;
/** How far a channel may be from the ground's and still be the ground: what JPEG does to a flat colour. */
const NEAR = 10;
/** How far a channel must be from the ground's to be the drawing. */
const APART = 28;
/** A drawing filling this much of its square needs no trimming. */
const FULL = 0.92;

export interface Fitted {
  /**
   * Where the drawing is in its picture, in the pixels of a picture `size` big. Null to
   * show the logo as it is. A box, not a cropped copy: the tile then shows that part of a
   * SHARP rendition, where a copy cut from the small one it was measured on came out soft.
   */
  readonly box: { x: number; y: number; width: number; height: number } | null;
  readonly size: { width: number; height: number } | null;
  /** The flat colour the logo was drawn on, which its tile should be too. Null when it is on nothing. */
  readonly ground: string | null;
}
const AS_IS: Fitted = { box: null, size: null, ground: null };

const fitted = new Map<string, Promise<Fitted>>();

function measureUrl(src: string): string | null {
  try {
    const url = new URL(src);
    if (url.hostname !== 'storage.flobi.ai' || url.search || url.pathname.startsWith('/cdn-cgi/') || /\.svg$/i.test(url.pathname)) return null;
    return `${url.origin}/cdn-cgi/image/${MEASURE}${url.pathname}`;
  } catch {
    return null;
  }
}

export function fitLogo(src: string): Promise<Fitted> {
  let made = fitted.get(src);
  if (made) return made;
  const from = measureUrl(src);
  made = !from
    ? Promise.resolve(AS_IS)
    : new Promise<Fitted>((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.onerror = () => resolve(AS_IS);
        image.onload = () => {
          try {
            const { naturalWidth: width, naturalHeight: height } = image;
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d', { willReadFrequently: true });
            if (!context || width === 0 || height === 0) return resolve(AS_IS);
            context.drawImage(image, 0, 0);
            const found = drawnBox(context.getImageData(0, 0, width, height).data, width, height);
            if (!found) return resolve(AS_IS);
            const { box, ground } = found;
            if (box.width >= width * FULL && box.height >= height * FULL) return resolve({ box: null, size: null, ground });
            resolve({ box, size: { width, height }, ground });
          } catch {
            // A tainted canvas, or a browser with no room for it: the logo as it is.
            resolve(AS_IS);
          }
        };
        image.src = from;
      });
  fitted.set(src, made);
  return made;
}

const hex = (r: number, g: number, b: number) => `#${[r, g, b].map((part) => Math.round(part).toString(16).padStart(2, '0')).join('')}`;

/**
 * The box a drawing fills in its square, in pixels — and the flat colour it sits on, when
 * it sits on one. Read off the corners: all four transparent is a drawing on nothing, all
 * four the same opaque colour is a drawing on that colour. Anything else (a photograph, a
 * drawing that runs to the edge) has no box to find.
 */
export function drawnBox(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number): { box: { x: number; y: number; width: number; height: number }; ground: string | null } | null {
  if (width < 2 || height < 2) return null;
  const at = (x: number, y: number) => (y * width + x) * 4;
  const corners = [at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1)];
  const clear = corners.every((i) => rgba[i + 3]! < SOLID);
  const first = corners[0]!;
  const flat = !clear && corners.every((i) => rgba[i + 3]! >= 250 && [0, 1, 2].every((c) => Math.abs(rgba[i + c]! - rgba[first + c]!) <= NEAR));
  if (!clear && !flat) return null;
  const ground = flat ? [0, 1, 2].map((c) => corners.reduce((sum, i) => sum + rgba[i + c]!, 0) / 4) : null;
  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = at(x, y);
      const drawn = ground ? [0, 1, 2].some((c) => Math.abs(rgba[i + c]! - ground[c]!) > APART) : rgba[i + 3]! >= SOLID;
      if (!drawn) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return null;
  return { box: { x: left, y: top, width: right - left + 1, height: bottom - top + 1 }, ground: ground ? hex(ground[0]!, ground[1]!, ground[2]!) : null };
}

/**
 * A rendition sharp enough for a band on a dense screen. A tile's own (320 px) is soft
 * once a logo is shown at twice its size with its margins cut away.
 */
export function sharpUrl(src: string): string {
  try {
    const url = new URL(src);
    if (url.hostname !== 'storage.flobi.ai' || url.search || url.pathname.startsWith('/cdn-cgi/') || /\.svg$/i.test(url.pathname)) return src;
    return `${url.origin}/cdn-cgi/image/width=960,fit=scale-down,format=auto,quality=85${url.pathname}`;
  } catch {
    return src;
  }
}

/** How to show a logo in a tile: as it is at once, then its drawing alone — and its own ground — when they have been found. */
export function useFittedLogo(logo: { url: string; vector: boolean } | null): Fitted & { src: string | null } {
  const [found, setFound] = useState<{ of: string; fit: Fitted } | null>(null);
  const url = logo && !logo.vector ? logo.url : null;
  useEffect(() => {
    if (!url) return;
    let live = true;
    void fitLogo(url).then((fit) => {
      if (live) setFound({ of: url, fit });
    });
    return () => {
      live = false;
    };
  }, [url]);
  const fit = found && found.of === url ? found.fit : AS_IS;
  return { ...fit, src: logo ? (logo.vector ? logo.url : sharpUrl(logo.url)) : null };
}
